import { useEffect, useState } from "react";
import { Loader2, Package, StickyNote, Tag, Trash2, User, X } from "lucide-react";
import toast from "react-hot-toast";
import { Autocomplete } from "../../../components/ui/Autocomplete";
import { BaseModal } from "../../../components/modals/BaseModal";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { handleApi } from "../../../utils/handleApi";
import {
  addContactNote,
  deleteContact,
  getContactByPhone,
  listContactTags,
  setContactTags,
  updateContactAlias,
  upsertContact,
  type ContactDetail,
} from "../../../services/whatsapp/whatsappService";

interface Props {
  onClose: () => void;
  phone: string | null;
  displayName?: string | null;
  onContactDeleted?: () => void;
  /** Fired after any successful write (alias, tags, notes, delete) so the
   *  parent can refresh the chat list without waiting for a full page reload. */
  onContactChanged?: () => void;
}

const ContactDetailPanel = ({
  onClose,
  phone,
  displayName,
  onContactDeleted,
  onContactChanged,
}: Props) => {
  const { language } = useAuth();
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [alias, setAlias] = useState("");
  const [newNote, setNewNote] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [tagCatalog, setTagCatalog] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!phone) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const load = async () => {
    if (!phone) return;

    setLoading(true);
    setContact(null);
    const existing = await getContactByPhone(phone);
    let resolved: ContactDetail | null = existing;
    if (!resolved) {
      const { res } = await handleApi(
        upsertContact(phone, displayName ?? null),
        false
      );
      if (res) {
        const { res: detailRes } = await handleApi(
          getContactByPhone(phone),
          false
        );
        resolved = detailRes || null;
      }
    }

    if (resolved) {
      setContact(resolved);
      setAlias(resolved.alias || "");
    }

    const { res: tagsRes } = await handleApi(listContactTags(), false);
    if (tagsRes) setTagCatalog(tagsRes);

    setLoading(false);
  };

  const handleSaveAlias = async () => {
    if (!contact) return;
    setSavingAlias(true);
    const trimmed = alias.trim();
    const { res } = await handleApi(
      updateContactAlias(contact.id, trimmed || null),
      false
    );
    if (res) {
      setContact({ ...contact, ...res });
      toast.success(dictionary["contact-alias-saved"]?.[language] ?? "Alias guardado");
      onContactChanged?.();
    } else {
      toast.error(dictionary["contact-info-error"]?.[language] ?? "Error al guardar");
    }
    setSavingAlias(false);
  };

  const handleAddNote = async () => {
    if (!contact || !newNote.trim()) return;
    setSavingNote(true);
    const { res } = await handleApi(
      addContactNote(contact.id, newNote.trim(), "human"),
      false
    );
    if (res) {
      setContact({ ...contact, notes: res.notes });
      setNewNote("");
      onContactChanged?.();
    } else {
      toast.error(dictionary["contact-note-error"]?.[language] ?? "No se pudo guardar la nota");
    }
    setSavingNote(false);
  };

  const handleTagsChange = async (tags: string[]) => {
    if (!contact) return;
    setContact({ ...contact, tags });
    const { res } = await handleApi(
      setContactTags(contact.id, tags),
      false
    );
    if (res) {
      setContact({ ...contact, ...res });
      const { res: refreshed } = await handleApi(listContactTags(), false);
      if (refreshed) setTagCatalog(refreshed);
      onContactChanged?.();
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    setDeleting(true);
    const { error } = await handleApi(deleteContact(contact.id), false);
    setDeleting(false);
    setShowDeleteModal(false);
    if (!error) {
      toast.success(dictionary["contact-deleted"]?.[language] ?? "Contacto eliminado");
      onContactDeleted?.();
      onContactChanged?.();
      onClose();
    } else {
      toast.error(dictionary["contact-delete-error"]?.[language] ?? "No se pudo eliminar");
    }
  };

  const tagOptions = Array.from(
    new Set([...(contact?.tags || []), ...tagCatalog])
  ).map((t) => ({ value: t, label: t }));

  return (
    <>
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-900">
            {dictionary["contact-panel-title"]?.[language] ?? "Detalle del contacto"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : !contact ? (
            <div className="text-sm text-gray-500 text-center py-10">
              {dictionary["contact-not-found"]?.[language] ?? "No se pudo cargar el contacto"}
            </div>
          ) : (
            <div className="space-y-6">
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {dictionary["contact-section-info"]?.[language] ?? "Información general"}
                  </h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                      {dictionary["contact-name-label"]?.[language] ?? "Nombre (último pedido)"}
                    </label>
                    <div className="mt-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                      {contact.name || <span className="text-gray-400 italic">{dictionary["contact-none"]?.[language] ?? "Sin datos"}</span>}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                      {dictionary["contact-email-label"]?.[language] ?? "Email (último pedido con email)"}
                    </label>
                    <div className="mt-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 break-all">
                      {contact.email || <span className="text-gray-400 italic">{dictionary["contact-none"]?.[language] ?? "Sin datos"}</span>}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                      {dictionary["contact-phone-label"]?.[language] ?? "Teléfono"}
                    </label>
                    <div className="mt-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                      +{contact.phone}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                      {dictionary["contact-alias-label"]?.[language] ?? "Alias (visible en chats)"}
                    </label>
                    <p className="text-[11px] text-gray-500 mt-1 mb-1">
                      {dictionary["contact-alias-hint"]?.[language] ?? "Si lo llenas, se usará este nombre en vez del nombre del pedido."}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                        placeholder={dictionary["contact-alias-placeholder"]?.[language] ?? "Ej: buena gente"}
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#595EEE]"
                      />
                      <button
                        type="button"
                        onClick={handleSaveAlias}
                        disabled={savingAlias || alias.trim() === (contact.alias || "")}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-800 disabled:opacity-50"
                      >
                        {savingAlias && <Loader2 className="w-3 h-3 animate-spin" />}
                        {dictionary["contact-save"]?.[language] ?? "Guardar"}
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {dictionary["contact-section-tags"]?.[language] ?? "Etiquetas"}
                  </h3>
                </div>
                <Autocomplete
                  multi
                  value={contact.tags}
                  onChange={handleTagsChange}
                  options={tagOptions}
                  allowCreate
                  placeholder={dictionary["contact-tags-placeholder"]?.[language] ?? "Agregar etiqueta..."}
                  emptyMessage={dictionary["contact-tags-empty"]?.[language] ?? "Sin etiquetas guardadas"}
                />
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {dictionary["contact-section-history"]?.[language] ?? "Historial de pedidos"}
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      ({contact.order_history.length})
                    </span>
                  </h3>
                </div>
                {contact.order_history.length === 0 ? (
                  <div className="text-xs text-gray-400">
                    {dictionary["contact-history-empty"]?.[language] ?? "Aún no hay pedidos"}
                  </div>
                ) : (
                  <ul className="space-y-2 max-h-72 overflow-y-auto pr-1 border border-gray-100 rounded-lg p-2 bg-gray-50/40">
                    {contact.order_history.map((o) => (
                      <li
                        key={o.order_id}
                        className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">#{o.order_id}</span>
                          <span className="text-gray-500">{o.status}</span>
                        </div>
                        <div className="text-gray-600 truncate mt-0.5">
                          {o.product_name || "—"}
                        </div>
                        <div className="flex justify-between items-center mt-1 text-gray-500">
                          <span>{o.created_at?.slice(0, 10) || ""}</span>
                          <span className="font-medium text-gray-900">${o.total}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {dictionary["contact-section-notes"]?.[language] ?? "Notas"}
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      ({contact.notes.length})
                    </span>
                  </h3>
                </div>
                <div className="space-y-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={dictionary["contact-note-placeholder"]?.[language] ?? "Agregar una nota..."}
                    rows={2}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#595EEE]"
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={savingNote || !newNote.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-800 disabled:opacity-50"
                  >
                    {savingNote && <Loader2 className="w-3 h-3 animate-spin" />}
                    {dictionary["contact-note-add"]?.[language] ?? "Agregar nota"}
                  </button>
                  <ul className="space-y-2 pt-2 max-h-60 overflow-y-auto">
                    {contact.notes.slice().reverse().map((n, idx) => (
                      <li
                        key={idx}
                        className={`text-xs rounded-lg px-3 py-2 ${
                          n.author === "ai"
                            ? "bg-[#595EEE]/5 border border-[#595EEE]/20"
                            : "bg-gray-50 border border-gray-200"
                        }`}
                      >
                        <div className="flex justify-between items-center mb-0.5 text-[10px] uppercase tracking-wide">
                          <span
                            className={
                              n.author === "ai" ? "text-[#595EEE] font-semibold" : "text-gray-500 font-semibold"
                            }
                          >
                            {n.author === "ai"
                              ? dictionary["contact-note-author-ai"]?.[language] ?? "IA"
                              : dictionary["contact-note-author-human"]?.[language] ?? "Tú"}
                          </span>
                          <span className="text-gray-400">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{n.content}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-red-500 hover:text-white transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {dictionary["contact-delete"]?.[language] ?? "Eliminar contacto"}
                </button>
              </section>
            </div>
          )}
        </div>
      </div>

      <BaseModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={dictionary["contact-delete-title"]?.[language] ?? "Eliminar contacto"}
        description={
          dictionary["contact-delete-description"]?.[language] ??
          "Esta acción liberará el cupo de contacto en tu plan. ¿Quieres continuar?"
        }
        buttonOne={{
          text: dictionary["contact-delete-confirm"]?.[language] ?? "Eliminar",
          onClick: handleDelete,
          loading: deleting,
          variant: "destructive",
        }}
        buttonTwo={{
          text: dictionary["contact-delete-cancel"]?.[language] ?? "Cancelar",
          onClick: () => setShowDeleteModal(false),
        }}
      />
    </>
  );
};

export default ContactDetailPanel;
